/**
 * Unified Authorization API
 *
 * Single entry point for authorization that combines two active layers:
 *
 * 1. CAPABILITY LAYER: Does the user have the base permission?
 *    - Resolved by permissionResolver.canSync (cascade_mode-aware)
 *    - The WHERE is embedded: cascade_mode on the role determines whether a role
 *      assignment at a given scope applies at the requested scope.
 *
 * 2. POLICY LAYER: Does the context allow this action?
 *    - Uses policyEvaluator
 *    - Ownership, state, timing, phase rules
 *
 * The former "scope layer" (System 2 / scope_kind / checkScopedPermission) has been
 * removed. The capability layer already answers both WHAT and WHERE via cascade_mode.
 */

import type { PermissionAction, PermissionScope } from '../../types/prioritization/permissions';
import type { ActorRole } from '../../types/prioritization/roles';
import type { CapabilityId } from '../../types/prioritization/capabilityIds';
import { getCapabilitiesForAction } from '../../types/prioritization/capabilityIds';
import type { EnterpriseUserProfile, OrgUnit } from '../../types/prioritization/enterpriseIdentity';
import type {
  ActorContext,
  ActionContext,
  ResourceContext,
  EvaluationContext,
  PolicyDecision,
  PolicyRule,
  ExplainResult,
} from './types';
import { evaluateAuthorization, explainDecision } from './policyEvaluator';
import { roleHasPermission } from '../../types/prioritization/rolePermissionMapping';

// ============================================================================
// Types
// ============================================================================

/**
 * Result from unified authorization check
 */
export interface UnifiedAuthResult {
  /** Whether the action is allowed */
  allowed: boolean;
  
  /** Which layer blocked (if denied) */
  blockedBy?: 'capability' | 'scope' | 'policy';
  
  /** Reason code for the decision */
  reasonCode: string;
  
  /** Human-readable reason */
  reason: string;
  
  /** Full policy decision (if policy layer was evaluated) */
  policyDecision?: PolicyDecision;
  
  /** User-friendly explanation */
  explanation?: ExplainResult;
  
  /** Debug info for logging */
  debug?: {
    capabilityPassed: boolean;
    scopePassed: boolean;
    policyPassed: boolean;
    effectiveRole?: ActorRole;
    effectiveScope?: PermissionScope;
  };
}

/**
 * Input for unified authorization check
 */
export interface UnifiedAuthInput {
  // User context (required)
  userId: string;
  /** Legacy: used when actorCapabilityIds not provided. Prefer actorCapabilityIds for capability-only checks. */
  userRoles: ActorRole[];
  isAdmin?: boolean;
  /** Resolved capability IDs for the actor (role → capabilities once; use this for capability-only authorization) */
  actorCapabilityIds?: CapabilityId[];

  // Action context (required)
  action: PermissionAction;

  // Scope context (kept for API compatibility; no longer used for a separate scope check)
  targetScope?: PermissionScope;

  // Resource context (optional, for policy evaluation)
  resource?: {
    type: ResourceContext['type'];
    id: string;
    createdBy?: string;
    ownerId?: string;
    assigneeId?: string;
    shapingOwnerId?: string;
    /** @deprecated Use assigneeId or shapingOwnerId */
    stewardId?: string;
    isLocked?: boolean;
    lockedFields?: string[];
    isPublished?: boolean;
    lifecycleState?: string;
    attributes?: Record<string, unknown>;
  };

  // Business context (optional, for policy evaluation)
  context?: {
    phase?: string;
    cycleId?: string;
    sessionId?: string;
    submissionWindowOpen?: boolean;
    contributionsFrozen?: boolean;
    /** Attribute governance and other policy-specific data */
    extra?: Record<string, unknown>;
  };

  // Kept for API compatibility; no longer used for scope resolution
  userProfile?: EnterpriseUserProfile;
  orgUnits?: Map<string, OrgUnit>;

  // Policy rules (optional, for policy evaluation)
  policyRules?: PolicyRule[];
}

// ============================================================================
// Default Policy Rules
// ============================================================================

/**
 * Default policy rules that apply to all authorization checks.
 * These implement the standard business rules.
 */
export const DEFAULT_POLICY_RULES: PolicyRule[] = [
  // Rule 1: System admins can do anything
  {
    id: 'admin-override',
    effect: 'allow',
    actions: ['*'],
    when: (ctx) => ctx.actor.isAdmin === true,
    reasons: () => [{ code: 'ADMIN_OVERRIDE' }],
    description: 'System administrators have full access',
  },
  
  // Rule 2: Owners can edit their own resources
  {
    id: 'owner-edit',
    effect: 'allow',
    actions: ['items.edit', 'items.edit.*'],
    resourceTypes: ['item'],
    when: (ctx) => {
      const isOwner = ctx.resource.ownerId === ctx.actor.userId ||
                      ctx.resource.createdBy === ctx.actor.userId;
      const notLocked = !ctx.resource.lockState?.isLocked;
      const notPublished = !ctx.resource.isPublished;
      return isOwner && notLocked && notPublished;
    },
    reasons: () => [{ code: 'OWNER_EDIT_ALLOWED' }],
    description: 'Owners can edit their own unlocked, unpublished items',
  },
  
  // Rule 3: Assignees (assigneeId / shaping owner) can edit assigned items
  {
    id: 'assignee-edit',
    effect: 'allow',
    actions: ['items.edit', 'items.edit.*'],
    resourceTypes: ['item'],
    when: (ctx) => {
      const resource = ctx.resource as { assigneeId?: string; shapingOwnerId?: string; stewardId?: string };
      const assigneeId = resource.assigneeId ?? resource.shapingOwnerId ?? resource.stewardId;
      const isAssignee = assigneeId === ctx.actor.userId;
      const notLocked = !ctx.resource.lockState?.isLocked;
      return isAssignee && notLocked;
    },
    reasons: () => [{ code: 'STEWARD_ACCESS' }],
    description: 'Assignees (the person assigned to the item) can edit items they are assigned to',
  },
  
  // Rule 4: Deny if resource is locked
  {
    id: 'locked-resource-deny',
    effect: 'deny',
    actions: ['items.edit', 'items.edit.*', 'items.delete'],
    resourceTypes: ['item'],
    when: (ctx) => ctx.resource.lockState?.isLocked === true,
    reasons: (ctx) => [{ 
      code: 'RESOURCE_LOCKED', 
      data: { 
        lockedBy: ctx.resource.lockState?.lockedBy,
        reason: ctx.resource.lockState?.lockReason,
      } 
    }],
    description: 'Locked resources cannot be modified',
  },
  
  // Rule 5: Deny if field is specifically locked
  {
    id: 'locked-field-deny',
    effect: 'deny',
    actions: ['items.edit.*'],
    resourceTypes: ['item'],
    when: (ctx) => {
      const actionField = ctx.action.key.split('.')[2]; // e.g., 'items.edit.content' -> 'content'
      return ctx.resource.lockState?.lockedFields?.includes(actionField) === true;
    },
    reasons: (ctx) => [{ 
      code: 'FIELD_LOCKED', 
      data: { field: ctx.action.key.split('.')[2] } 
    }],
    description: 'Locked fields cannot be modified',
  },
  
  // Rule 6: Deny if published
  {
    id: 'published-deny',
    effect: 'deny',
    actions: ['items.edit', 'items.edit.*', 'items.delete'],
    resourceTypes: ['item'],
    when: (ctx) => ctx.resource.isPublished === true,
    reasons: () => [{ code: 'ALREADY_PUBLISHED' }],
    description: 'Published items cannot be modified',
  },
  
  // Rule 7: Deny submissions when window closed
  {
    id: 'submission-window-closed',
    effect: 'deny',
    actions: ['ideas.submit'],
    when: (ctx) => ctx.context.submissionWindowOpen === false,
    reasons: () => [{ code: 'SUBMISSION_WINDOW_CLOSED' }],
    description: 'Cannot submit when window is closed',
  },
  
  // Rule 8: Deny when contributions frozen
  {
    id: 'contributions-frozen',
    effect: 'deny',
    actions: ['items.edit', 'items.edit.*', 'values.*'],
    when: (ctx) => ctx.context.contributionsFrozen === true,
    reasons: () => [{ code: 'CONTRIBUTIONS_FROZEN' }],
    description: 'Cannot contribute when frozen',
  },
  
  // Rule 9: Default allow if capability passed
  {
    id: 'default-allow',
    effect: 'allow',
    actions: ['*'],
    when: () => true, // Always matches as fallback
    reasons: () => [{ code: 'ROLE_ALLOWED' }],
    description: 'Default allow if capability and scope checks passed',
  },
];

// ============================================================================
// Main API
// ============================================================================

/**
 * Check authorization using all three layers.
 * 
 * @param input - Authorization check parameters
 * @returns UnifiedAuthResult with the decision and explanation
 */
export function checkAuthorization(input: UnifiedAuthInput): UnifiedAuthResult {
  const {
    userId,
    userRoles,
    isAdmin = false,
    actorCapabilityIds,
    action,
    targetScope = { kind: 'organization', id: '*' },
    resource,
    context,
    policyRules = DEFAULT_POLICY_RULES,
  } = input;

  // ========================================================================
  // Layer 1: Capability Check
  // The cascade_mode-aware permissionResolver embeds the WHERE constraint when
  // loading capabilities, so a passing capability check means the user has the
  // right to perform the action at the relevant scope.
  // ========================================================================

  let capabilityPassed = false;
  let effectiveRole: ActorRole | undefined;

  if (actorCapabilityIds && actorCapabilityIds.length > 0) {
    const required = getCapabilitiesForAction(action);
    capabilityPassed = required.length === 0 || required.some((c) => actorCapabilityIds.includes(c));
  } else {
    for (const role of userRoles) {
      if (roleHasPermission(role, action)) {
        capabilityPassed = true;
        effectiveRole = role;
        break;
      }
    }
  }

  if (isAdmin) {
    capabilityPassed = true;
  }

  if (!capabilityPassed) {
    return {
      allowed: false,
      blockedBy: 'capability',
      reasonCode: 'MISSING_CAPABILITY',
      reason: `No role grants permission: ${action}`,
      debug: {
        capabilityPassed: false,
        scopePassed: false,
        policyPassed: false,
      },
    };
  }

  // ========================================================================
  // Layer 2 (Policy Check)
  // ========================================================================

  // If no resource context provided, skip policy evaluation
  if (!resource) {
    return {
      allowed: true,
      reasonCode: 'ROLE_ALLOWED',
      reason: 'Permission granted (no policy context provided)',
      debug: {
        capabilityPassed: true,
        scopePassed: true,
        policyPassed: true,
        effectiveRole,
        effectiveScope: targetScope,
      },
    };
  }

  const actorContext: ActorContext & { capabilityIds?: CapabilityId[] } = {
    userId,
    roleIds: userRoles,
    isAdmin,
  };
  if (actorCapabilityIds && actorCapabilityIds.length > 0) {
    actorContext.capabilityIds = actorCapabilityIds;
  }

  const actionContext: ActionContext = { key: action };

  const resourceContext: ResourceContext = {
    type: resource.type,
    id: resource.id,
    createdBy: resource.createdBy,
    ownerId: resource.ownerId,
    assigneeId: resource.assigneeId ?? resource.shapingOwnerId ?? resource.stewardId,
    isPublished: resource.isPublished,
    lifecycleState: resource.lifecycleState,
    lockState: resource.isLocked ? {
      isLocked: true,
      lockedFields: resource.lockedFields,
    } : undefined,
    attributes: resource.attributes,
    scope: targetScope,
  };

  const evalContext: EvaluationContext = {
    now: new Date(),
    phase: context?.phase,
    cycleId: context?.cycleId,
    submissionWindowOpen: context?.submissionWindowOpen,
    contributionsFrozen: context?.contributionsFrozen,
  };

  const policyDecision = evaluateAuthorization(
    actorContext,
    actionContext,
    resourceContext,
    evalContext,
    policyRules,
    () => true // capability already verified above
  );

  const explanation = explainDecision(policyDecision);

  return {
    allowed: policyDecision.allowed,
    blockedBy: policyDecision.allowed ? undefined : 'policy',
    reasonCode: policyDecision.reasons[0]?.code || (policyDecision.allowed ? 'ALLOWED' : 'DENIED'),
    reason: explanation.summary,
    policyDecision,
    explanation,
    debug: {
      capabilityPassed: true,
      scopePassed: true,
      policyPassed: policyDecision.allowed,
      effectiveRole,
      effectiveScope: targetScope,
    },
  };
}

/**
 * Quick capability-only check (skips scope and policy layers).
 * Use for fast UI decisions where full context isn't available.
 */
export function hasCapability(
  userRoles: ActorRole[],
  action: PermissionAction,
  isAdmin: boolean = false
): boolean {
  if (isAdmin) return true;
  return userRoles.some(role => roleHasPermission(role, action));
}

/**
 * Check authorization and return a user-friendly explanation.
 * Useful for showing "why is this disabled?" in the UI.
 */
export function explainAuthorization(input: UnifiedAuthInput): ExplainResult {
  const result = checkAuthorization(input);
  
  if (result.explanation) {
    return result.explanation;
  }
  
  // Build explanation from result
  return {
    allowed: result.allowed,
    summaryKey: `reason.${result.reasonCode}`,
    summary: result.reason,
    details: result.debug,
    remediationHints: result.allowed ? undefined : [
      {
        key: 'contact-admin',
        message: 'Contact an administrator for assistance.',
      },
    ],
  };
}

/**
 * Higher-order function to create a permission checker bound to a user context.
 * Useful for React components that need to check multiple permissions.
 */
export function createAuthChecker(
  userId: string,
  userRoles: ActorRole[],
  isAdmin: boolean = false,
  userProfile?: EnterpriseUserProfile,
  orgUnits?: Map<string, OrgUnit>
) {
  return {
    /**
     * Check if user can perform action
     */
    can: (action: PermissionAction, scope?: PermissionScope): boolean => {
      return checkAuthorization({
        userId,
        userRoles,
        isAdmin,
        action,
        targetScope: scope,
        userProfile,
        orgUnits,
      }).allowed;
    },
    
    /**
     * Check with full context including resource and policies
     */
    canWithContext: (
      action: PermissionAction,
      resource?: UnifiedAuthInput['resource'],
      context?: UnifiedAuthInput['context'],
      policyRules?: PolicyRule[]
    ): UnifiedAuthResult => {
      return checkAuthorization({
        userId,
        userRoles,
        isAdmin,
        action,
        resource,
        context,
        userProfile,
        orgUnits,
        policyRules,
      });
    },
    
    /**
     * Get explanation for why action is allowed/denied
     */
    explain: (
      action: PermissionAction,
      resource?: UnifiedAuthInput['resource'],
      context?: UnifiedAuthInput['context']
    ): ExplainResult => {
      return explainAuthorization({
        userId,
        userRoles,
        isAdmin,
        action,
        resource,
        context,
        userProfile,
        orgUnits,
      });
    },
  };
}
